import {
  Activity,
  AlertTriangle,
  LayoutGrid,
  Sparkles,
  TrendingUp,
  UserRound,
  type LucideIcon,
} from "lucide-react";

import type {
  OverviewSectionId,
  OverviewSignalSource,
  OverviewSyncProvider,
  RoadmapLaneKey,
} from "@/lib/six-pillars-preview";

/** The product's issue / opportunity / strength tints (B6 `SECTION_STYLES`). */
export const SECTION_STYLES: Record<
  OverviewSectionId,
  {
    label: string;
    singular: string;
    icon: LucideIcon;
    badgeClassName: string;
    iconClassName: string;
    dotClassName: string;
  }
> = {
  issues: {
    label: "Issues",
    singular: "Issue",
    icon: AlertTriangle,
    badgeClassName: "border-rose-200 bg-rose-100/80 text-rose-900",
    iconClassName: "text-rose-600",
    dotClassName: "bg-rose-600",
  },
  opportunities: {
    label: "Opportunities",
    singular: "Opportunity",
    icon: TrendingUp,
    badgeClassName: "border-sky-200 bg-sky-100/80 text-sky-900",
    iconClassName: "text-sky-600",
    dotClassName: "bg-sky-600",
  },
  strengths: {
    label: "Strengths",
    singular: "Strength",
    icon: Sparkles,
    badgeClassName: "border-emerald-200 bg-emerald-100/80 text-emerald-900",
    iconClassName: "text-emerald-600",
    dotClassName: "bg-emerald-600",
  },
};

/** Horizon badges and 2px rules: Today in slate with a black rule, then sky, violet and amber. */
export const LANE_STYLES: Record<
  RoadmapLaneKey,
  {
    label: string;
    shortLabel: string;
    badgeClassName: string;
    ruleClassName: string;
    dotClassName: string;
  }
> = {
  today: {
    label: "Today",
    shortLabel: "Today",
    badgeClassName: "border-slate-200 bg-slate-100 text-slate-900",
    ruleClassName: "border-foreground",
    dotClassName: "bg-foreground",
  },
  this_week: {
    label: "This week",
    shortLabel: "Week",
    badgeClassName: "border-sky-200 bg-sky-100/80 text-sky-900",
    ruleClassName: "border-sky-300",
    dotClassName: "bg-sky-500",
  },
  this_month: {
    label: "This month",
    shortLabel: "Month",
    badgeClassName: "border-violet-200 bg-violet-100/80 text-violet-900",
    ruleClassName: "border-violet-300",
    dotClassName: "bg-violet-500",
  },
  this_quarter: {
    label: "This quarter",
    shortLabel: "Quarter",
    badgeClassName: "border-amber-200 bg-amber-100/80 text-amber-900",
    ruleClassName: "border-amber-300",
    dotClassName: "bg-amber-500",
  },
};

export const SIGNAL_SOURCES: Record<
  OverviewSignalSource,
  { label: string; icon: LucideIcon }
> = {
  profiles: { label: "Profiles", icon: UserRound },
  fundamentals: { label: "Fundamentals", icon: LayoutGrid },
  pulse: { label: "Pulse", icon: Activity },
};

export const SIGNAL_SOURCE_ORDER: OverviewSignalSource[] = [
  "profiles",
  "fundamentals",
  "pulse",
];

export const SYNC_PROVIDER_LABELS: Record<OverviewSyncProvider, string> = {
  asana: "Asana",
  notion: "Notion",
  clickup: "ClickUp",
  linear: "Linear",
};

export const RISK_TONES: Record<"low" | "medium" | "high", string> = {
  low: "border-emerald-200 bg-emerald-100/80 text-emerald-900",
  medium: "border-amber-200 bg-amber-100/80 text-amber-900",
  high: "border-rose-200 bg-rose-100/80 text-rose-900",
};

/** The board's one easing: a fast start that settles softly. */
export const OVERVIEW_EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}
